import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, ArrowUp, ArrowDown, ChevronRight, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { categoryService, operationLogService } from '@/services/storage';
import type { ProductCategory } from '@/types';
import { toast } from 'sonner';

export default function AdminCategories() {
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    description: '',
    sortOrder: 1,
    parentId: '',
    image: '',
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    const all = await categoryService.getAll();
    setCategories(all);
  };

  const getTopLevelCategories = () => {
    return categories.filter(c => !c.parentId).sort((a, b) => a.sortOrder - b.sortOrder);
  };

  const getChildCategories = (parentId: string) => {
    return categories.filter(c => c.parentId === parentId).sort((a, b) => a.sortOrder - b.sortOrder);
  };

  const handleAdd = (parentId: string = '') => {
    setEditingCategory(null);
    const maxOrder = categories.length > 0 
      ? Math.max(...categories.map(c => c.sortOrder)) + 1 
      : 1;
    setFormData({
      name: '',
      nameEn: '',
      description: '',
      sortOrder: maxOrder,
      parentId,
      image: '',
    });
    setDialogOpen(true);
  };

  const handleEdit = (category: ProductCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      nameEn: category.nameEn,
      description: category.description,
      sortOrder: category.sortOrder,
      parentId: category.parentId || '',
      image: category.image || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('确定要删除此分类吗？相关的子分类也会被删除。')) {
      const cat = categories.find(c => c.id === id);
      await categoryService.delete(id);
      await await operationLogService.add({
        user: 'admin',
        action: '删除分类',
        module: '分类管理',
        details: `删除分类: ${cat?.name || id}`,
      });
      toast.success('分类已删除');
      loadCategories();
    }
  };

  const handleMoveUp = async (category: ProductCategory) => {
    const siblings = categories.filter(c => c.parentId === category.parentId);
    const sorted = [...siblings].sort((a, b) => a.sortOrder - b.sortOrder);
    const index = sorted.findIndex(c => c.id === category.id);
    
    if (index > 0) {
      const prevCategory = sorted[index - 1];
      // 交换排序号
      await categoryService.update(category.id, { sortOrder: prevCategory.sortOrder });
      await categoryService.update(prevCategory.id, { sortOrder: category.sortOrder });
      
      await operationLogService.add({
        user: 'admin',
        action: '调整分类顺序',
        module: '分类管理',
        details: `将 "${category.name}" 上移`,
      });
      
      loadCategories();
      toast.success('顺序已调整');
    }
  };

  const handleMoveDown = async (category: ProductCategory) => {
    const siblings = categories.filter(c => c.parentId === category.parentId);
    const sorted = [...siblings].sort((a, b) => a.sortOrder - b.sortOrder);
    const index = sorted.findIndex(c => c.id === category.id);
    
    if (index < sorted.length - 1) {
      const nextCategory = sorted[index + 1];
      // 交换排序号
      categoryService.update(category.id, { sortOrder: nextCategory.sortOrder });
      categoryService.update(nextCategory.id, { sortOrder: category.sortOrder });
      
      await operationLogService.add({
        user: 'admin',
        action: '调整分类顺序',
        module: '分类管理',
        details: `将 "${category.name}" 下移`,
      });
      
      loadCategories();
      toast.success('顺序已调整');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.nameEn) {
      toast.error('请填写必填项');
      return;
    }

    if (editingCategory) {
      await categoryService.update(editingCategory.id, {
        name: formData.name,
        nameEn: formData.nameEn,
        description: formData.description,
        sortOrder: formData.sortOrder,
        parentId: formData.parentId || undefined,
        image: formData.image,
      });
      await operationLogService.add({
        user: 'admin',
        action: '编辑分类',
        module: '分类管理',
        details: `编辑分类: ${formData.name}`,
      });
      toast.success('分类已更新');
    } else {
      const newCategory: ProductCategory = {
        id: Date.now().toString(),
        name: formData.name,
        nameEn: formData.nameEn,
        description: formData.description,
        sortOrder: formData.sortOrder,
        parentId: formData.parentId || undefined,
        image: formData.image,
      };
      await categoryService.add(newCategory);
      await operationLogService.add({
        user: 'admin',
        action: '添加分类',
        module: '分类管理',
        details: `添加分类: ${formData.name}`,
      });
      toast.success('分类已添加');
    }

    setDialogOpen(false);
    loadCategories();
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      toast.error('请上传图片文件（JPG、PNG、GIF、WebP）');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('图片大小不能超过5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, image: reader.result as string });
      toast.success('图片上传成功');
    };
    reader.readAsDataURL(file);
  };

  const renderCategoryRow = (category: ProductCategory, level: number = 0): React.ReactNode => {
    const children = getChildCategories(category.id);
    const isTopLevel = !category.parentId;
    const siblings = categories.filter(c => c.parentId === category.parentId);
    const sortedSiblings = [...siblings].sort((a, b) => a.sortOrder - b.sortOrder);
    const currentIndex = sortedSiblings.findIndex(c => c.id === category.id);
    const isFirst = currentIndex === 0;
    const isLast = currentIndex === sortedSiblings.length - 1;

    return (
      <React.Fragment key={category.id}>
        <TableRow className={level > 0 ? 'bg-gray-50' : ''}>
          <TableCell>
            <div className="flex items-center" style={{ paddingLeft: `${level * 24}px` }}>
              {level > 0 && <ChevronRight className="h-4 w-4 mr-2 text-gray-400" />}
              <span className="font-medium">{category.name}</span>
            </div>
          </TableCell>
          <TableCell>{category.nameEn}</TableCell>
          <TableCell>{category.sortOrder}</TableCell>
          <TableCell>{category.description}</TableCell>
          <TableCell className="text-right">
            <div className="flex items-center justify-end space-x-1">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => handleMoveUp(category)}
                disabled={isFirst}
                className={isFirst ? 'opacity-30' : ''}
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => handleMoveDown(category)}
                disabled={isLast}
                className={isLast ? 'opacity-30' : ''}
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
              {isTopLevel && (
                <Button variant="ghost" size="sm" onClick={() => handleAdd(category.id)}>
                  <Plus className="h-4 w-4" />
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => handleEdit(category)}>
                <Edit className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(category.id)}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          </TableCell>
        </TableRow>
        {children.map(child => renderCategoryRow(child, level + 1))}
      </React.Fragment>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">分类管理</h1>
        <Button onClick={() => handleAdd()}>
          <Plus className="mr-2 h-5 w-5" />
          添加分类
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>分类名称</TableHead>
                <TableHead>英文名称</TableHead>
                <TableHead>排序</TableHead>
                <TableHead>描述</TableHead>
                <TableHead className="text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getTopLevelCategories().map(category => renderCategoryRow(category))}
            </TableBody>
          </Table>

          {categories.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              暂无分类数据
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? '编辑分类' : '添加分类'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">
                分类名称 <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="如LDO线性稳压器"
                required
              />
            </div>
            <div>
              <Label htmlFor="nameEn">
                英文名称 <span className="text-red-500">*</span>
              </Label>
              <Input
                id="nameEn"
                value={formData.nameEn}
                onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                placeholder="如LDO"
                required
              />
            </div>
            <div>
              <Label htmlFor="sortOrder">排序号</Label>
              <Input
                id="sortOrder"
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 1 })}
                placeholder="数字越小排序越靠前"
              />
              <p className="text-xs text-gray-500 mt-1">数字越小排序越靠前，建议"其他产品"设置为较大数字如99</p>
            </div>
            <div>
              <Label htmlFor="description">描述</Label>
              <textarea
                id="description"
                className="w-full border rounded-md px-3 py-2"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="请输入分类描述"
              />
            </div>

            <div>
              <Label>
                <span className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  分类图片
                </span>
              </Label>
              <div className="mt-2">
                {formData.image && (
                  <img
                    src={formData.image}
                    alt="分类图片"
                    className="w-full h-32 object-cover rounded mb-2"
                  />
                )}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
                <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                取消
              </Button>
              <Button type="submit">
                <Save className="mr-2 h-4 w-4" />
                保存
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
